import { useAppSelector } from "app/hooks";
import { endOfWeek, format, startOfWeek } from "date-fns";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { useMemo } from "react";
import { useGetEventsQuery } from "services/events";
import { EventFilters } from "services/types/events.type";
import { isStaffMemberAvailableUtil } from "utils/build-staff-member-work-shift.util";
import { EVENTS_QUERY } from "../hooks/use-appointments.hook";

type useStaffMemberScheduleProps = {
  appointmentStartDate?: Date;
  appointmentEndDate?: Date;
  staffMemberId: number | null;
  clinicId?: number;
};
export default function useStaffMemberSchedule({
  appointmentStartDate,
  appointmentEndDate,
  staffMemberId,
  clinicId,
}: useStaffMemberScheduleProps) {
  const currentClinic = useAppSelector(selectCurrentClinic);

  const filters: EventFilters = useMemo(() => {
    return {
      baseFilters: {},
      fromDate: format(
        startOfWeek(appointmentStartDate || new Date()),
        "yyyy-MM-dd"
      ),
      toDate: format(endOfWeek(appointmentEndDate || new Date()), "yyyy-MM-dd"),
      staffIds: staffMemberId ? [staffMemberId] : [],
      clinicIds: [clinicId || currentClinic?.id],
      query: EVENTS_QUERY,
    };
  }, [
    appointmentStartDate,
    appointmentEndDate,
    currentClinic?.id,
    clinicId,
    staffMemberId,
  ]);

  const {
    data: events,
    isUninitialized,
    isSuccess,
  } = useGetEventsQuery(filters, {
    skip: staffMemberId === null || !(clinicId || currentClinic?.id),
  });
  const { workShift = undefined } = events || {};
  if (!appointmentStartDate || !appointmentEndDate || !staffMemberId) {
    return {
      isAvailable: false,
      isUninitialized,
      isSuccess,
    };
  }

  const result = isStaffMemberAvailableUtil({
    staffMemberId,
    clinicId: clinicId ? clinicId : currentClinic?.id,
    startDate: new Date(appointmentStartDate),
    endDate: new Date(appointmentEndDate),
    workShift,
  });
  return {
    isAvailable: result,
    isUninitialized,
    isSuccess,
  };
}
